/**
 * ProgressionBuilder — the structured editor for a scenario's authored
 * progression. Presentational: the page owns the draft and the server calls.
 *
 * Every reference is chosen from what the scenario declares (its phases, its
 * objection concerns, the registered rubrics), never typed free-hand, and the
 * server's validation problems are shown against the section they concern.
 */

import type { ReactNode } from 'react';
import {
  CONDITION_LABELS,
  conditionNeedsRef,
  conditionNeedsThreshold,
  groupProblems,
  MODALITY_LABELS,
  RUBRIC_MODALITIES,
  rubricGradesModality,
  removePhase,
  type AuthoringConcern,
  type Condition,
  type ConditionKind,
  type ProblemSection,
  type ProgressionDraft,
  type RubricSummary,
} from './progressionTypes';

export interface ProgressionBuilderProps {
  draft: ProgressionDraft;
  onChange: (next: ProgressionDraft) => void;
  concerns: AuthoringConcern[];
  /** Phases the scenario's objections wait for: the progression must declare them. */
  requiredPhases: string[];
  actionKinds: string[];
  conditionKinds: ConditionKind[];
  rubrics: RubricSummary[];
  /** Server validation problems (422, or a stored definition that no longer validates). */
  problems: string[];
  disabled?: boolean;
}

const input =
  'block w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100';
const smallBtn = 'rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50';

function Problems({ items, section }: { items: string[]; section: ProblemSection }) {
  if (!items.length) return null;
  return (
    <ul className="mt-2 space-y-1" data-testid={`problems-${section}`} role="alert">
      {items.map((p) => (
        <li key={p} className="rounded bg-red-50 px-2 py-1 text-xs text-red-700">
          {p}
        </li>
      ))}
    </ul>
  );
}

function Section({ title, hint, children, problems, section }: {
  title: string; hint?: string; children: ReactNode; problems: string[]; section: ProblemSection;
}) {
  return (
    <section
      className={`rounded-lg border p-4 ${problems.length ? 'border-red-300' : 'border-gray-200'}`}
      data-section={section}
    >
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
      <div className="mt-3 space-y-2">{children}</div>
      <Problems items={problems} section={section} />
    </section>
  );
}

function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function ConditionRow({ condition, phases, concerns, conditionKinds, onChange, onRemove, idPrefix, disabled }: {
  condition: Condition; phases: string[]; concerns: AuthoringConcern[]; conditionKinds: ConditionKind[];
  onChange: (c: Condition) => void; onRemove: () => void; idPrefix: string; disabled?: boolean;
}) {
  const refKind = conditionNeedsRef(condition.kind);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={idPrefix}>
      <select
        aria-label="Condition kind"
        className={`${input} w-auto`}
        value={condition.kind}
        disabled={disabled}
        onChange={(e) => onChange({ kind: e.target.value as ConditionKind, description: condition.description })}
      >
        {conditionKinds.map((k) => (
          <option key={k} value={k}>{CONDITION_LABELS[k] ?? k}</option>
        ))}
      </select>
      {refKind && (
        <select
          aria-label={refKind === 'phase' ? 'Phase' : 'Concern'}
          className={`${input} w-auto`}
          value={condition.ref ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ ...condition, ref: e.target.value || undefined })}
        >
          <option value="">Choose {refKind}…</option>
          {refKind === 'phase'
            ? phases.map((p) => <option key={p} value={p}>{p}</option>)
            : concerns.map((c) => <option key={c.ref} value={c.ref}>{c.ref}: {c.summary}</option>)}
        </select>
      )}
      {conditionNeedsThreshold(condition.kind) && (
        <input
          aria-label="Threshold"
          type="number"
          min={1}
          className={`${input} w-32`}
          value={condition.threshold ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ ...condition, threshold: e.target.value === '' ? undefined : Number(e.target.value) })}
        />
      )}
      <input
        aria-label="Rationale"
        placeholder="Rationale (optional, never evaluated)"
        className={`${input} min-w-0 flex-1`}
        value={condition.description ?? ''}
        disabled={disabled}
        onChange={(e) => onChange({ ...condition, description: e.target.value })}
      />
      <button type="button" className={smallBtn} onClick={onRemove} disabled={disabled}>Remove</button>
    </div>
  );
}

export function ProgressionBuilder(props: ProgressionBuilderProps) {
  const { draft, onChange, concerns, requiredPhases, actionKinds, conditionKinds, rubrics, disabled } = props;
  const grouped = groupProblems(props.problems);
  const set = (patch: Partial<ProgressionDraft>) => onChange({ ...draft, ...patch });
  const firstKind: ConditionKind = conditionKinds[0] ?? 'phase_reached';

  const conditionList = (key: 'successConditions' | 'failureConditions') => (
    <>
      {draft[key].map((c, i) => (
        <ConditionRow
          key={i}
          idPrefix={`${key}-${i}`}
          condition={c}
          phases={draft.phases}
          concerns={concerns}
          conditionKinds={conditionKinds}
          disabled={disabled}
          onChange={(next) => set({ [key]: draft[key].map((x, j) => (j === i ? next : x)) })}
          onRemove={() => set({ [key]: draft[key].filter((_, j) => j !== i) })}
        />
      ))}
      <button type="button" className={smallBtn} disabled={disabled}
        onClick={() => set({ [key]: [...draft[key], { kind: firstKind }] })}>
        Add condition
      </button>
    </>
  );

  return (
    <div className="space-y-4" data-testid="progression-builder">
      {grouped.general.length > 0 && <Problems items={grouped.general} section="general" />}

      <Section title="Phases" section="phases" problems={grouped.phases}
        hint={`Ordered; every attempt starts in the first. ${requiredPhases.length ? `This scenario's objections wait for: ${requiredPhases.join(', ')}.` : ''}`}>
        {draft.phases.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-6 text-xs text-gray-400">{i + 1}.</span>
            <input aria-label={`Phase ${i + 1}`} className={input} value={p} disabled={disabled}
              onChange={(e) => {
                const old = draft.phases[i];
                const name = e.target.value;
                const transitions: ProgressionDraft['transitions'] = {};
                for (const [from, to] of Object.entries(draft.transitions)) {
                  transitions[from === old ? name : from] = to ? to.map((t) => (t === old ? name : t)) : null;
                }
                set({ phases: draft.phases.map((x, j) => (j === i ? name : x)), transitions });
              }} />
            <button type="button" className={smallBtn} disabled={disabled || i === 0}
              onClick={() => { const ph = [...draft.phases]; [ph[i - 1], ph[i]] = [ph[i]!, ph[i - 1]!]; set({ phases: ph }); }}>Up</button>
            <button type="button" className={smallBtn} disabled={disabled || i === draft.phases.length - 1}
              onClick={() => { const ph = [...draft.phases]; [ph[i + 1], ph[i]] = [ph[i]!, ph[i + 1]!]; set({ phases: ph }); }}>Down</button>
            <button type="button" className={smallBtn} disabled={disabled} onClick={() => onChange(removePhase(draft, i))}>Remove</button>
          </div>
        ))}
        <button type="button" className={smallBtn} disabled={disabled} onClick={() => set({ phases: [...draft.phases, ''] })}>Add phase</button>
      </Section>

      <Section title="Allowed transitions" section="transitions" problems={grouped.transitions}
        hint="Forward only. Unrestricted means any later phase (the engine default).">
        {draft.phases.slice(0, -1).map((from, i) => {
          const later = draft.phases.slice(i + 1);
          const authored = draft.transitions[from] ?? null;
          return (
            <div key={`${from}-${i}`} className="text-sm" data-testid={`transitions-${from}`}>
              <label className="flex items-center gap-2 font-medium text-gray-700">
                <input type="checkbox" checked={authored !== null} disabled={disabled || !from}
                  onChange={(e) => set({ transitions: { ...draft.transitions, [from]: e.target.checked ? [...later] : null } })} />
                From <code>{from || '(unnamed)'}</code>: {authored === null ? 'any later phase' : 'only'}
              </label>
              {authored !== null && (
                <div className="ml-6 mt-1 flex flex-wrap gap-3">
                  {later.map((to) => (
                    <label key={to} className="flex items-center gap-1 text-gray-600">
                      <input type="checkbox" checked={authored.includes(to)} disabled={disabled}
                        onChange={() => set({ transitions: { ...draft.transitions, [from]: toggle(authored, to) } })} />
                      {to}
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </Section>

      <Section title="Facts" section="facts" problems={grouped.facts}
        hint="Authored truths the persona may disclose. Ids must be unique.">
        {draft.facts.map((f, i) => (
          <div key={i} className="flex items-center gap-2">
            <input aria-label={`Fact ${i + 1} id`} placeholder="id" className={`${input} w-40`} value={f.id} disabled={disabled}
              onChange={(e) => set({ facts: draft.facts.map((x, j) => (j === i ? { ...x, id: e.target.value } : x)) })} />
            <input aria-label={`Fact ${i + 1} statement`} placeholder="statement" className={input} value={f.statement} disabled={disabled}
              onChange={(e) => set({ facts: draft.facts.map((x, j) => (j === i ? { ...x, statement: e.target.value } : x)) })} />
            <button type="button" className={smallBtn} disabled={disabled}
              onClick={() => set({ facts: draft.facts.filter((_, j) => j !== i) })}>Remove</button>
          </div>
        ))}
        <button type="button" className={smallBtn} disabled={disabled}
          onClick={() => set({ facts: [...draft.facts, { id: '', statement: '' }] })}>Add fact</button>
      </Section>

      <Section title="Required concerns" section="requiredConcernRefs" problems={grouped.requiredConcernRefs}
        hint="From the scenario persona's authored objections. Not authored: concerns of intensity 3+ are required.">
        {concerns.length === 0 ? (
          <p className="text-xs text-gray-500">This scenario's persona has no authored objections, so there are no concerns to require.</p>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={draft.requiredConcernRefs !== null} disabled={disabled}
                onChange={(e) => set({ requiredConcernRefs: e.target.checked ? [] : null })} />
              Author the required concerns explicitly
            </label>
            {draft.requiredConcernRefs !== null && concerns.map((c) => (
              <label key={c.ref} className="ml-6 flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={draft.requiredConcernRefs!.includes(c.ref)} disabled={disabled}
                  onChange={() => set({ requiredConcernRefs: toggle(draft.requiredConcernRefs ?? [], c.ref) })} />
                <code>{c.ref}</code> {c.summary} <span className="text-xs text-gray-400">(intensity {c.intensity}{c.phase ? `, from ${c.phase}` : ''})</span>
              </label>
            ))}
          </>
        )}
      </Section>

      <Section title="Allowed actions" section="allowedActions" problems={grouped.allowedActions}
        hint="Not authored: every engine action is permitted.">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={draft.allowedActions !== null} disabled={disabled}
            onChange={(e) => set({ allowedActions: e.target.checked ? [...actionKinds] : null })} />
          Restrict the actions this scenario permits
        </label>
        {draft.allowedActions !== null && (
          <div className="ml-6 flex flex-wrap gap-3">
            {actionKinds.map((a) => (
              <label key={a} className="flex items-center gap-1 text-sm text-gray-600">
                <input type="checkbox" checked={draft.allowedActions!.includes(a)} disabled={disabled}
                  onChange={() => set({ allowedActions: toggle(draft.allowedActions ?? [], a) })} />
                {a}
              </label>
            ))}
          </div>
        )}
      </Section>

      <Section title="Success conditions" section="successConditions" problems={grouped.successConditions}
        hint="All must hold for the attempt to be eligible. At least one is required.">
        {conditionList('successConditions')}
      </Section>

      <Section title="Failure conditions" section="failureConditions" problems={grouped.failureConditions}
        hint="Any holding makes the attempt not eligible.">
        {conditionList('failureConditions')}
      </Section>

      <Section title="Rubric" section="rubric" problems={grouped.rubric}
        hint="What a graded (Testing) attempt is assessed against. A modality without an applicable rubric can be practised, but not taken as a test.">
        <div className="flex flex-wrap gap-4 text-sm" role="radiogroup" aria-label="Rubric applicability">
          <label className="flex items-center gap-1">
            <input type="radio" name="rubric-form" disabled={disabled} checked={draft.modalityRubrics === null}
              onChange={() => set({ modalityRubrics: null })} />
            One rubric for Written Chat and Live Conversation
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" name="rubric-form" data-testid="per-modality-toggle" disabled={disabled} checked={draft.modalityRubrics !== null}
              onChange={() => set({ modalityRubrics: {}, rubric: null })} />
            A rubric per modality
          </label>
        </div>
        {draft.modalityRubrics === null ? (
          <select aria-label="Rubric" className={input} disabled={disabled}
            value={draft.rubric ? `${draft.rubric.id}@${draft.rubric.version}` : ''}
            onChange={(e) => {
              const r = rubrics.find((x) => `${x.id}@${x.version}` === e.target.value);
              set({ rubric: r ? { id: r.id, version: r.version } : null });
            }}>
            <option value="">No rubric (not gradable)</option>
            {rubrics.map((r) => (
              <option key={`${r.id}@${r.version}`} value={`${r.id}@${r.version}`} disabled={!r.supportsScenarioModalities}>
                {r.title} ({r.id}@{r.version}){r.scope === 'general_communication' ? ' — general communication' : ''}
                {!r.supportsScenarioModalities ? ' — needs written and voice support; use a rubric per modality' : ''}
              </option>
            ))}
            {draft.rubric && !rubrics.some((r) => r.id === draft.rubric!.id && r.version === draft.rubric!.version) && (
              <option value={`${draft.rubric.id}@${draft.rubric.version}`}>Unregistered: {draft.rubric.id}@{draft.rubric.version}</option>
            )}
          </select>
        ) : (
          <div className="space-y-2" data-testid="modality-rubrics">
            {RUBRIC_MODALITIES.map((m) => {
              const chosen = draft.modalityRubrics?.[m] ?? null;
              return (
                <label key={m} className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="w-56 text-gray-700">{MODALITY_LABELS[m]}</span>
                  <select aria-label={`Rubric for ${MODALITY_LABELS[m]}`} className={input} disabled={disabled}
                    value={chosen ? `${chosen.id}@${chosen.version}` : ''}
                    onChange={(e) => {
                      const r = rubrics.find((x) => `${x.id}@${x.version}` === e.target.value);
                      const next = { ...(draft.modalityRubrics ?? {}) };
                      if (r) next[m] = { id: r.id, version: r.version };
                      else delete next[m];
                      set({ modalityRubrics: next });
                    }}>
                    <option value="">Not offered for Testing</option>
                    {rubrics.map((r) => {
                      const g = rubricGradesModality(r, m);
                      return (
                        <option key={`${r.id}@${r.version}`} value={`${r.id}@${r.version}`} disabled={!g.ok}>
                          {r.title} ({r.id}@{r.version}){g.ok ? '' : ` — ${g.why}`}
                        </option>
                      );
                    })}
                    {chosen && !rubrics.some((r) => r.id === chosen.id && r.version === chosen.version) && (
                      <option value={`${chosen.id}@${chosen.version}`}>Unregistered: {chosen.id}@{chosen.version}</option>
                    )}
                  </select>
                </label>
              );
            })}
            <p className="text-xs text-gray-500">
              Only the modalities given a rubric can be taken as a graded test. No rubric is assumed to cover every modality.
            </p>
          </div>
        )}
        {(() => {
          const r = draft.rubric && rubrics.find((x) => x.id === draft.rubric!.id && x.version === draft.rubric!.version);
          if (!r) return null;
          return (
            <div className="text-xs text-gray-600" data-testid="rubric-detail">
              {r.scope === 'general_communication' && (
                <p className="mb-1 text-amber-700">General communication rubric: it judges how the user communicated, not whether this scenario's goal was met.</p>
              )}
              <ul className="list-disc pl-5">
                {r.dimensions.map((d) => <li key={d.key}><strong>{d.label}</strong>: {d.description}</li>)}
              </ul>
            </div>
          );
        })()}
      </Section>
    </div>
  );
}
